import Seo from "@/components/feature/Seo";
import Layout from "@/components/feature/Layout";
import ComicCard from "@/components/feature/ComicCard";
import SectionHeader from "@/components/base/SectionHeader";
import { comics, categories, todayUpdates, getComicById } from "@/mocks/comics";
import { useNavigate } from "react-router-dom";

export default function Home() {
  const navigate = useNavigate();

  const newComics = comics.filter((c) => c.isNew);
  const hotComics = comics.filter((c) => c.isHot);
  const completed = comics.filter((c) => c.status === "已完结");
  const serial = comics.filter((c) => c.status === "连载中").slice(0, 8);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: "笨狗漫画网",
        url: "https://manhwanalysis.github.io/",
        description:
          "笨狗漫画网每日更新列表一目了然，汇聚最新漫画、连载与完结作品，追更不迷路。",
        inLanguage: "zh-CN",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://manhwanalysis.github.io/comics?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "ItemList",
        itemListElement: comics.slice(0, 10).map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.title,
          url: `https://manhwanalysis.github.io/comic/${c.id}`,
        })),
      },
    ],
  };

  return (
    <Layout>
      <Seo
        title="笨狗漫画网官网 | 今日更新列表 · 最新漫画 · 连载与完结作品一览"
        description="笨狗漫画网每日更新列表一目了然，汇聚最新漫画、连载作品与完结作品，今日新上架内容点开即达。追更不迷路，快速找到你想看的漫画，随时掌握漫画更新动态。"
        keywords="笨狗漫画网,今日更新,最新漫画,连载漫画,完结漫画,新上架漫画,漫画更新列表"
        canonical="https://manhwanalysis.github.io/"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-background-100">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=colorful%20animated%20comic%20book%20hero%20illustration%20with%20floating%20panels%20and%20comic%20speech%20bubbles%2C%20young%20readers%20enjoying%20manga%2C%20warm%20orange%20and%20cream%20youthful%20palette%2C%20playful%20flat%20illustration%20style%2C%20vibrant%20and%20inviting%20atmosphere%2C%20soft%20depth%20of%20field&width=1800&height=900&seq=hero-banner&orientation=landscape"
            alt="笨狗漫画网漫画阅读平台插画"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 via-background-950/50 to-background-950/30"></div>
        </div>

        <div className="relative mx-auto flex w-full max-w-7xl flex-col items-start px-4 py-20 md:px-6 md:py-28">
          <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-line mr-1"></i>今日更新已就绪
          </span>
          <h1 className="mt-5 max-w-2xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            追更不迷路，<br />
            今日漫画更新 <span className="text-primary-400">一目了然</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
            最新漫画、连载作品、完结作品随时掌握。点开就能看到今日新上架内容，快速找到你想看的漫画，随时掌握更新动态。
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate("/updates")}
              className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              查看今日更新 <i className="ri-arrow-right-line ml-1"></i>
            </button>
            <button
              onClick={() => navigate("/comics")}
              className="whitespace-nowrap rounded-full border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 cursor-pointer"
            >
              浏览漫画库
            </button>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-6">
            {[
              { label: "连载作品", value: "2,860+" },
              { label: "完结作品", value: "1,940+" },
              { label: "每日更新", value: "500+" },
            ].map((s) => (
              <div key={s.label}>
                <p className="font-heading text-2xl font-extrabold text-background-50">{s.value}</p>
                <p className="mt-1 text-xs text-background-100/80">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 今日更新列表 */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <SectionHeader
          title="今日更新列表"
          subtitle="最新连载动态实时汇总，追更不迷路"
          accent
        />
        <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="hidden grid-cols-12 gap-4 border-b border-background-200 bg-background-100 px-5 py-3 text-xs font-semibold text-foreground-600 md:grid">
            <span className="col-span-6">漫画作品</span>
            <span className="col-span-3">更新章节</span>
            <span className="col-span-3 text-right">更新时间</span>
          </div>
          <ul>
            {todayUpdates.slice(0, 8).map((u) => {
              const comic = getComicById(u.comicId);
              if (!comic) return null;
              return (
                <li
                  key={u.comicId}
                  className="grid grid-cols-12 items-center gap-3 border-b border-background-100 px-4 py-3 transition-colors last:border-0 hover:bg-background-50 md:px-5 cursor-pointer"
                  onClick={() => navigate(`/comic/${comic.id}`)}
                >
                  <div className="col-span-12 flex items-center gap-3 md:col-span-6">
                    <img
                      src={comic.cover}
                      alt={`${comic.title}封面`}
                      className="h-12 w-9 rounded-md object-cover"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-foreground-950">
                        {comic.title}
                      </p>
                      <p className="truncate text-xs text-foreground-500">
                        {comic.author} · {comic.status}
                      </p>
                    </div>
                  </div>
                  <div className="col-span-9 truncate text-sm text-primary-700 md:col-span-3">
                    {u.chapterTitle}
                  </div>
                  <div className="col-span-3 flex items-center justify-end gap-2 text-xs text-foreground-500 md:col-span-3">
                    <span className="rounded-full bg-accent-100 px-2 py-0.5 text-accent-700">
                      {u.time}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="mt-4 text-center">
          <button
            onClick={() => navigate("/updates")}
            className="whitespace-nowrap rounded-full border border-primary-300 px-6 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
          >
            查看完整更新列表 <i className="ri-arrow-right-line ml-1"></i>
          </button>
        </div>
      </section>

      {/* 最新上架 */}
      <section className="mx-auto max-w-7xl px-4 pb-14 md:px-6">
        <SectionHeader
          title="最新上架"
          subtitle="新作抢先看，第一时间发现好漫画"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {newComics.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      {/* 热门连载 */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <SectionHeader
            title="热门连载"
            subtitle="近期热度最高的连载作品，大家都在追"
            accent
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {hotComics.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
            {serial.slice(0, 2).map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* 完结作品推荐 */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <SectionHeader
          title="完结作品推荐"
          subtitle="一口气看到结局的宝藏作品"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {completed.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      {/* 分类导航 */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <SectionHeader
            title="漫画分类"
            subtitle="按类型浏览，快速锁定感兴趣的作品"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => navigate(`/comics?cat=${encodeURIComponent(cat)}`)}
                className="group flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 p-4 text-left transition-all hover:-translate-y-1 hover:border-primary-300 cursor-pointer"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className="ri-grid-fill text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-bold text-foreground-950">{cat}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {cat.includes("热血") || cat.includes("奇幻")
                      ? "人气连载聚集地"
                      : "精选优质作品"}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-14 md:px-6">
        <SectionHeader title="常见问题" subtitle="关于追更与浏览的常见疑问" />
        <div className="space-y-3">
          {[
            {
              q: "笨狗漫画网多久更新一次？",
              a: "本站每日持续更新，重点连载作品通常会在当天不定时刷新章节，新上架内容会第一时间同步到「今日更新列表」，方便你随时掌握最新动态。",
            },
            {
              q: "如何快速找到连载中的漫画？",
              a: "你可以在「漫画分类」页面通过关键词搜索，或按连载中、已完结、新上架等状态筛选，也能直接点击「今日更新」查看当天所有连载作品的更新章节。",
            },
            {
              q: "完结作品还能继续阅读吗？",
              a: "当然可以。完结作品会在「完结作品推荐」板块长期展示，所有章节均完整保留，你可以一口气看到结局，无需担心内容下架。",
            },
            {
              q: "收藏的漫画更新后如何第一时间知道？",
              a: "关注该作品后，每次章节更新都会体现在「今日更新列表」的最新条目中，并按照更新时间排序，让你追更不迷路。",
            },
          ].map((item) => (
            <details
              key={item.q}
              className="group rounded-xl border border-background-200 bg-background-50 px-5 py-4 open:bg-background-50"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-foreground-950">
                {item.q}
                <i className="ri-add-line text-lg text-foreground-500 transition-transform group-open:rotate-45"></i>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.a}</p>
            </details>
          ))}
        </div>
      </section>
    </Layout>
  );
}